import type { IntlShape } from '@formatjs/intl'
import { showErrorToast } from './toast'

/**
 * t() signature mirrored from useLanguage().
 */
type TFn = (key: string, params?: Parameters<IntlShape['formatMessage']>[1]) => string

type FormatParams = Parameters<IntlShape['formatMessage']>[1]

interface ApiErrorPayload {
  code?: string
  message?: string
  params?: FormatParams
  statusCode?: number
}

interface TranslatedError {
  title: string
  message: string
}

/**
 * Map an API error into a user-facing title + message.
 *
 * The API responds with `{ code, message, params, statusCode }`. We map
 * `code` to a translation key under `errors.<code>` and pass `params` to
 * `t()` for templated copy. The English `message` from the envelope is the
 * fallback when no translation exists yet — it's also what surfaces in
 * Sentry / dev tools.
 */
export function translateApiError(t: TFn, error: unknown): TranslatedError {
  const data = ((error as { response?: { data?: ApiErrorPayload } })?.response?.data) ?? undefined
  const title = t('errors.title')

  if (data?.code) {
    const key = `errors.${data.code}`
    const translated = t(key, data.params)
    // @formatjs's formatMessage falls back to the message id when the key
    // is missing. Detect that and use the API's English fallback instead.
    if (translated && translated !== key) {
      return { title, message: translated }
    }
    if (data.message) return { title, message: data.message }
  }

  const fallback =
    (error as { message?: string } | undefined)?.message ?? t('errors.generic')
  return { title, message: fallback }
}

/**
 * Convenience: translate then show as an error toast.
 */
export function showApiErrorToast(t: TFn, error: unknown): void {
  const { title, message } = translateApiError(t, error)
  showErrorToast(title, message)
}
